// responsible for any overall 'page' changes
// any page change needs to be done via this module,
// instead of using $('body').html()

import $ from "jquery";
import * as preact from "preact";

import * as aiConsent from "aiConsent";
import * as analytics from "analytics";
import type { MainLayoutProps } from "AppRootView";
import * as appShell from "appShell";
import * as currentDate from "currentDate";
import * as deviceDetect from "deviceDetect";
import * as genericModals from "genericModals";
import * as modalController from "modalController";
import * as nativeHeader from "nativeHeader";
import * as notificationUtils from "notificationUtils";
import type PreactPage from "pages/PreactPage";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import { userProfilePath } from "shared/usernameValidation";
import * as utils from "shared/utils";
import * as sharedPage from "sharedPage";
import * as sharePrompt from "sharePrompt";
import * as signupModal from "signupModal";
import stores from "stores/stores";
import * as themeColor from "themeColor";
import * as urlUtils from "urlUtils";
import { markUserFetched, markUserStale } from "userFetchThrottle";
import ChangePasswordDialog from "views/ChangePasswordDialog";
import { EditProfile } from "views/EditProfileView";
import * as walkthrough from "walkthrough";
import * as webFontLoader from "webFontLoader";
import * as webReaderInstaller from "webReaderInstaller";

let headerAndFooterVisible = false;
let containerElement;
let mainLayoutProps: MainLayoutProps | undefined;
let appRouter;
let page;
let options;
let flashcardHintShownThisSession = false;
let flashcardHintReadyToStartCount: number | undefined;
let flashcardHintVisible = false;
let trackedAppLoad = false;

// The server reads utm parameters from the Referer header (see
// src/emailUtm.js), but some pages rewrite the URL before Load App fires
// (e.g. /learn, /library/:id → /library/:id/from/:position), dropping them.
// Snapshot the landing URL's at module load, before the router dispatches.
const landingUtmProperties: Record<string, string> = {};
for (const [key, value] of new URLSearchParams(window.location.search)) {
  if (key.startsWith("utm_")) {
    landingUtmProperties[key] = value;
  }
}

export const init = (_appRouter) => {
  appRouter = _appRouter;

  webFontLoader.init();
  walkthrough.init(stores.user);
  notificationUtils.init(stores);
  sharePrompt.init();

  stores.user.onChange(
    (user) => [
      user.username,
      user.displayName,
      user.activeLanguages,
      user.subscriptionPlan,
      user.googlePlusInfo,
      user.hasPassword,
    ],
    () => {
      void refreshMainLayoutProps();
    }
  );
  stores.user.onChange(
    (a) => a.firstLanguage,
    () => {
      updateFonts();
      void refreshMainLayoutProps();
    }
  );
  stores.user.onChange(
    (a) => a.currentLearningLanguage,
    () => {
      syncUrlLanguageSegment();
      updateFonts();
      void refreshMainLayoutProps();
    }
  );
  window.alert = (message) => {
    genericModals.alert({ message: message });
  };
};

const updatePromoLink = () => {
  // delay to ensure the web reader has time to write the #readlang-extension-installed div
  setTimeout(() => {
    void refreshMainLayoutProps();
  }, 800);
};

// No `trigger` — the language-scoped pages already refetch their content via
// their own currentLearningLanguage watchers, so only the URL needs updating.
const syncUrlLanguageSegment = () => {
  const newPath = urlUtils.pathWithLanguageSegmentReplaced(
    window.location.pathname,
    stores.user.state.value.currentLearningLanguage
  );
  if (newPath) {
    appRouter.navigate(newPath, { replace: true });
  }
};

const updateFonts = () => {
  const firstLanguage = languageMap.map[stores.user.state.value.firstLanguage];
  const currentLearningLanguage =
    languageMap.map[stores.user.state.value.currentLearningLanguage];

  if (firstLanguage.alphabet) {
    webFontLoader.loadSubset(firstLanguage.alphabet);
  }
  if (currentLearningLanguage.alphabet) {
    webFontLoader.loadSubset(currentLearningLanguage.alphabet);
  }
};

const showChangePasswordDialog = () => {
  modalController.cancel();
  modalController.show(preact.h(ChangePasswordDialog, {}), {
    title: "changePassword",
  });
};

const showProfileDialog = () => {
  modalController.show(
    preact.h(EditProfile, {
      finished: function () {
        modalController.cancel();
        modalController.cancel();
      },
    }),
    {
      title: "account",
    }
  );
};

/** Allows async function */
const onDocumentReady = (asyncCallback) => {
  $(document).ready(() => asyncCallback());
};

const trackRabbitShareDialog = () => {
  analytics.trackEvent("showRabbitShareDialog");
};

const dismissFlashcardHint = () => {
  flashcardHintVisible = false;
  walkthrough.setAchievement("flashcardsHint", true);
  void refreshMainLayoutProps();
};

const buildMainLayoutProps = async (userJSON): Promise<MainLayoutProps> => {
  const mainLayoutUser = {
    ...userJSON,
    upgraded:
      userJSON.subscriptionPlan === "standard" ||
      userJSON.subscriptionPlan === "plus" ||
      userJSON.subscriptionPlan === "freeForever",
  };

  mainLayoutUser.displayName = utils.elide(
    mainLayoutUser.displayName || mainLayoutUser.username,
    21
  );

  return {
    activeLanguages: stores.user.state.value.activeLanguages,
    flashcardHintReadyToStartCount,
    flashcardHintVisible,
    nativeHeader: nativeHeader.isEnabled(),
    onAccountClick: () => {
      modalController.cancel();
      appRouter.navigate("account", { trigger: true });
    },
    onAdminClick: () => {
      modalController.cancel();
      appRouter.navigate("admin", { trigger: true });
    },
    onDismissFlashcardHint: dismissFlashcardHint,
    onFirstLanguageSelect: (languageCode) => {
      stores.user.selectFirstLanguage(languageCode);
    },
    onForumClick: () => {
      modalController.cancel();
      window.open("https://forum.readlang.com", "_blank");
    },
    onLearningLanguageSelect: (languageCode) => {
      markUserFetched();
      stores.user.selectLearningLanguage(languageCode).catch(markUserStale);
    },
    onLogoClick: trackRabbitShareDialog,
    onLogoutClick: () => {
      readlangClient.logout();
    },
    onPracticeClick: () => {
      dismissFlashcardHint();
      appRouter.navigate("/learn", { trigger: true });
    },
    onProfileClick: () => {
      appRouter.navigate(
        userProfilePath(stores.user.state.value.username, stores.user.id.value),
        { trigger: true }
      );
    },
    onSettingsClick: () => {
      modalController.cancel();
      appRouter.navigate("settings", { trigger: true });
    },
    onSignInClick: () => {
      signupModal.show({
        heading: "Let's get started!",
        stores: stores,
      });
    },
    onUserPasswordClick: showChangePasswordDialog,
    onWordsClick: () => {
      appRouter.navigate("/words", { trigger: true });
    },
    showAdminPanel: stores.user.isGod.value,
    showStreak: await stores.user.hasNewStreakFeature(),
    showWebReaderInstaller: webReaderInstaller.showInstaller(),
    streakStore: stores.streak,
    user: mainLayoutUser,
  };
};

/** The iOS app draws the header natively, so tells it about any change */
const showNativeHeader = () => {
  if (nativeHeader.isEnabled() && page) {
    $("body").addClass("nativeHeader");
    nativeHeader.show(
      headerAndFooterVisible ? mainLayoutProps : undefined,
      page.name,
      appRouter
    );
  }
};

const refreshMainLayoutProps = async () => {
  if (!headerAndFooterVisible) {
    return;
  }

  mainLayoutProps = await buildMainLayoutProps(stores.user.viewJSON.value);
  showNativeHeader();
  await appShell.updateMainLayoutProps(mainLayoutProps);
};

// page should have a render function
export const renderPage = async (_page: PreactPage, _options?) => {
  let newPage = false;

  // close previous page
  if (page && page.name !== _page.name) {
    newPage = true;
    page.close?.();
  }

  modalController.cancelAll();

  $(".pageLoading").removeClass("pageLoading");

  page = _page;
  options = _options || {};

  // Don't empty #entirePage here — appShell owns the container via
  // preact.render and clears it on mount, and would lose its preact root if
  // the children were detached out from under it.

  // Execute async rendering logic without blocking
  await Promise.all([
    (async () => {
      if (!stores.user.isNew.value) return;
      try {
        await stores.user.fetch();
      } catch {
        throw "No user!";
      }
      if (
        !stores.user.state.value.firstLanguage ||
        !stores.user.state.value.currentLearningLanguage
      ) {
        showProfileDialog();
      }
    })(),
    (async () => {
      // fetch now so we don't have to wait later
      await currentDate.get();
    })(),
  ]);

  // referral code
  let referrer = urlUtils.getUrlVar("ref", window.location.hash);
  if (referrer) {
    localStorage.setItem("referrer", referrer);
  }

  if (!(await readlangClient.isLocalMode())) {
    referrer = localStorage.getItem("referrer");
    if (referrer) {
      stores.user
        .save({ referrer: referrer })
        .then(() => localStorage.removeItem("referrer"));
    }
  }

  const userJSON = stores.user.viewJSON.value;

  updateFonts();

  await new Promise<void>((resolve) => onDocumentReady(() => resolve()));

  const isLocalMode = await readlangClient.isLocalMode();
  const hasSetLanguages =
    walkthrough.hasAchievement("setLanguages") ||
    stores.user.state.value.currentLearningLanguage !== "es";

  if (isLocalMode && options.skipOnboardingDialogs) {
    // If we're skipping onboarding dialogs for this page, don't then show it on the next
    // page the user visits.
    localStorage.setItem("shownSignup", "true");
  }

  // The iOS app has no trial mode: signed-out users always go back to its
  // welcome screen ("/") to sign up or sign in.
  const isIOSApp = deviceDetect.isIOSApp();
  if (
    isLocalMode &&
    (isIOSApp ||
      (!options.skipOnboardingDialogs &&
        (!hasSetLanguages || !localStorage.getItem("shownSignup")))) &&
    window.location.pathname !== "/" && // The iOS app's welcome screen
    window.location.pathname !== "/start" &&
    window.location.pathname !== "/webReaderSignIn" &&
    !window.location.pathname.startsWith("/startLanguage")
  ) {
    // Reset these since we aren't going to be rendering that page. If we don't do this,
    // then mainLayout will try to call the page.close() function without having rendered it
    // first.
    page = undefined;
    options = undefined;
    appRouter.navigate(
      isIOSApp
        ? "/"
        : `/start?redirectPath=${encodeURIComponent(window.location.pathname)}`,
      { trigger: true }
    );
    return;
  }

  let title = page.name.replace("Page", "");
  title =
    title.substring(0, 1).toUpperCase() +
    title.substring(1).replace(/([A-Z])/g, " $1") + // "CamelCase" -> "Camel Case"
    " - Readlang";
  $("title").text(title);

  if (!page.showHeader) {
    $("body").removeClass("showHeader");
    containerElement = $("#entirePage");
    headerAndFooterVisible = false;
    renderPageContent(newPage);
    return;
  }

  // The header can stay mounted (even hidden behind pages without it), so its
  // streak doesn't load itself afresh
  void stores.streak.refetchIfAppropriate();

  mainLayoutProps = await buildMainLayoutProps(userJSON);

  if (headerAndFooterVisible) {
    await renderPageContent(newPage);
    return;
  }

  const body = $("body");
  body.addClass("showHeader");

  containerElement = $("#entirePage");
  headerAndFooterVisible = true;

  await renderPageContent(newPage);

  updatePromoLink();
  stores.practiceStore.emitGroupsUpdated();
};

/** Listening for touches lets them set :active, for the pressed look of buttons (_base.scss) */
const enableActiveStyles = () => {};

const renderPageContent = async (newPage: boolean) => {
  if (navigator.userAgent.match(/Android|iPhone|iPad|iPod/i)) {
    $("body").addClass("touch");
    document.addEventListener("touchstart", enableActiveStyles, {
      passive: true,
    });
  } else {
    // cspell: disable-next-line
    $("body").addClass("notouch");
  }
  if (newPage) {
    containerElement.addClass("pageLoading");
  }
  // Before the page has loaded, so that it's not seen first. A page shared
  // to the app waits for it.
  void aiConsent.requireInApp().then(sharedPage.open);
  await appShell.renderPage(
    containerElement,
    page,
    (renderedOptions) => {
      $("body").attr("id", page.name);
      if (newPage) {
        containerElement.removeClass("pageLoading");
      }
      renderedOptions = renderedOptions || {};

      if (page.showHeader && !renderedOptions.alreadyScrolled) {
        window.scrollTo(0, 0);
      }
      // set theme color based on body:
      themeColor.update();

      if (!trackedAppLoad) {
        analytics.trackEventWithProperties("loadApp", {
          ...landingUtmProperties,
          page: page.name,
          billingAddressNeeded: stores.user.state.value.billingAddressNeeded
            ? true
            : false,
        });
        trackedAppLoad = true;
      }

      void sharePrompt.pageRendered(page.name);

      if (!headerAndFooterVisible) {
        flashcardHintShownThisSession = false;
        flashcardHintReadyToStartCount = undefined;
        flashcardHintVisible = false;
      }
      if (
        headerAndFooterVisible &&
        !flashcardHintShownThisSession &&
        !/^\/learn/.test(window.location.pathname) &&
        !(
          walkthrough.hasAchievement("flashcards") ||
          walkthrough.hasAchievement("flashcardsHint")
        ) &&
        $(window).width() > 754
      ) {
        flashcardHintShownThisSession = true;
        readlangClient.request(
          "GET",
          "/api/readyToStartFlashcards",
          {
            language: stores.user.state.value.currentLearningLanguage,
          },
          (data) => {
            if (data.count) {
              if (data.count > 7) {
                flashcardHintReadyToStartCount = data.count;
                flashcardHintVisible = true;
                void refreshMainLayoutProps();
              }
            } else if (data.error === "alreadyStarted") {
              walkthrough.setAchievement("flashcards", true);
            }
          }
        );
      }
    },
    appRouter,
    options,
    page.showHeader ? mainLayoutProps : undefined,
    // Along with the page, once the app's begun sliding to it (pageTransitions.ts)
    showNativeHeader
  );
};
